/**
 * Configuration Option Hooks
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import {
  createConfigOption,
  deleteConfigOption,
  getAllConfigOptions,
  getConfigOptionListByCategory,
  updateConfigOption,
  uploadConfigOptionImageApi,
} from "../api/configOption";
import { queryKey } from "../constants";
import { IConfigOptionForm } from "../types/configOption";

/**
 * Hook to fetch option list for a category
 */
export const useConfigOptionList = (categoryId: string) => {
  return useQuery({
    queryKey: [queryKey.getConfigOptionList, categoryId],
    queryFn: () => getConfigOptionListByCategory(categoryId),
    enabled: !!categoryId,
  });
};

/**
 * Hook to create a new option
 */
export const useCreateConfigOption = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      categoryId,
      payload,
    }: {
      categoryId: string;
      payload: IConfigOptionForm;
    }) => createConfigOption(categoryId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [queryKey.getConfigOptionList, variables.categoryId],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAllConfigOptions],
      });
      toast.success("Option created successfully");
    },
  });
};

/**
 * Hook to update an existing option
 */
export const useUpdateConfigOption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
      categoryId: _categoryId,
    }: {
      id: string;
      payload: Partial<IConfigOptionForm>;
      categoryId?: string;
    }) => updateConfigOption({ id, payload }),
    onSuccess: (_, variables) => {
      if (variables.categoryId) {
        queryClient.invalidateQueries({
          queryKey: [queryKey.getConfigOptionList, variables.categoryId],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [queryKey.getConfigOptionList],
        });
      }
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAllConfigOptions],
      });
      toast.success("Option updated successfully");
    },
  });
};

/**
 * Hook to delete an option
 */
export const useDeleteConfigOption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, categoryId: _categoryId }: { id: string; categoryId?: string }) =>
      deleteConfigOption(id),
    onSuccess: (_, variables) => {
      if (variables.categoryId) {
        queryClient.invalidateQueries({
          queryKey: [queryKey.getConfigOptionList, variables.categoryId],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [queryKey.getConfigOptionList],
        });
      }
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAllConfigOptions],
      });
      toast.success("Option deleted successfully");
    },
  });
};

/**
 * Hook to fetch all options across all categories
 */
export const useAllConfigOptions = () => {
  return useQuery({
    queryKey: [queryKey.getAllConfigOptions],
    queryFn: () => getAllConfigOptions(),
  });
};

/**
 * Hook to upload configuration option image
 */
export const useUploadConfigOptionImage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      imageData,
      optionGuid,
      fileName,
      contentType,
    }: {
      imageData: File | Blob | ArrayBuffer;
      optionGuid: string;
      fileName: string;
      contentType: string;
      categoryId?: string;
    }) =>
      uploadConfigOptionImageApi(imageData, optionGuid, fileName, contentType),
    onSuccess: (_, variables) => {
      if (variables.categoryId) {
        queryClient.invalidateQueries({
          queryKey: [queryKey.getConfigOptionList, variables.categoryId],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [queryKey.getConfigOptionList],
        });
      }
      toast.success("Option image uploaded successfully");
    },
  });
};
